import { useEffect, useRef, useState } from 'react';
import { portalApi, formatApiError, viewProtectedFile } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SignaturePad } from '@/components/staff/SignaturePad';
import { fmtEdDate, fmtEdTime } from '@/lib/tz';
import { toast } from 'sonner';
import { CheckCircle2, Clock, Paperclip, Download, CalendarClock, FileSignature, FileText, AlertTriangle, PenLine, Type } from 'lucide-react';

const stamp = (d) => `${fmtEdDate(d, { month: 'short', day: 'numeric', year: 'numeric' })}, ${fmtEdTime(d)} MT`;

// Staff self-service list of admin-assigned activities. Completion is a one-tap acknowledge, a file
// upload, or a read-and-sign (typed or drawn), depending on how the admin set it up.
export default function PortalTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [fileTask, setFileTask] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [signTask, setSignTask] = useState(null);
  const [signMode, setSignMode] = useState('TYPED');
  const [signerName, setSignerName] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [signBusy, setSignBusy] = useState(false);
  const fileRef = useRef(null);
  const padRef = useRef(null);

  const load = () =>
    portalApi.get('/staff/me/tasks').then(({ data }) => setTasks(data.tasks)).finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const completeSimple = async (task) => {
    setBusyId(task.id);
    try {
      await portalApi.post(`/staff/me/tasks/${task.id}/complete`);
      toast.success('Marked complete');
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const completeWithFile = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error('Please choose a file');
    setUploadBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await portalApi.post(`/staff/me/tasks/${fileTask.id}/complete`, form);
      toast.success('Activity completed');
      setFileTask(null);
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadBusy(false);
    }
  };

  const openSign = (task) => {
    setSignTask(task);
    // Respect the admin-allowed signature method(s).
    setSignMode(task.signatureMethods === 'DRAWN' ? 'DRAWN' : 'TYPED');
    setSignerName('');
    setConfirmed(false);
  };

  const submitSign = async () => {
    if (!confirmed) return toast.error('Please confirm you have read the document');
    if (!signerName.trim()) return toast.error('Please enter your full name');
    let signatureImage = null;
    if (signMode === 'DRAWN') {
      if (!padRef.current || padRef.current.isEmpty()) return toast.error('Please draw your signature');
      signatureImage = padRef.current.getDataUrl();
    }
    setSignBusy(true);
    try {
      await portalApi.post(`/staff/me/tasks/${signTask.id}/sign`, {
        method: signMode,
        signerName: signerName.trim(),
        signatureImage,
        confirmedRead: true,
      });
      toast.success('Signed — thank you!');
      setSignTask(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSignBusy(false);
    }
  };

  const viewDoc = (task) => viewProtectedFile(portalApi, `/staff/tasks/${task.id}/source`).catch((err) => toast.error(formatApiError(err)));

  const pending = tasks.filter((t) => t.status !== 'COMPLETED');
  const done = tasks.filter((t) => t.status === 'COMPLETED');

  return (
    <div className="space-y-5" data-testid="portal-tasks">
      <div>
        <h1 className="font-heading text-2xl font-700 text-charcoal">My Activities</h1>
        <p className="text-sm text-muted-ink">Requirements assigned to you — complete them here.</p>
      </div>

      {loading ? (
        <p className="text-sm text-muted-ink">Loading…</p>
      ) : tasks.length === 0 ? (
        <div className="bg-white rounded-2xl border border-border/40 p-6 text-center text-sm text-muted-ink" data-testid="portal-tasks-empty">
          Nothing assigned right now. New activities from your manager will show up here.
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-ink uppercase tracking-wide">To do ({pending.length})</p>
            {pending.length === 0 && <p className="text-sm text-coriander flex items-center gap-1.5"><CheckCircle2 size={15} /> All caught up!</p>}
            {pending.map((t) => (
              <div key={t.id} className="bg-white rounded-2xl border border-border/40 p-4" data-testid="portal-task-row">
                <div className="flex items-start gap-2">
                  <Clock size={16} className="text-amber-500 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-charcoal">{t.title}{t.completionType === 'SIGN' && <FileSignature size={13} className="inline ml-1.5 text-muted-ink" />}{t.completionType === 'FILE' && <Paperclip size={13} className="inline ml-1.5 text-muted-ink" />}</p>
                    {t.description && <p className="text-xs text-muted-ink mt-0.5">{t.description}</p>}
                    {t.expired && <p className="text-xs text-amber-600 mt-1 flex items-center gap-1" data-testid="portal-task-expired"><AlertTriangle size={12} /> Your previous signature has expired — please re-sign.</p>}
                    <p className="text-xs text-muted-ink mt-1 flex flex-wrap gap-x-2">
                      {t.category && <span>{t.category}</span>}
                      {t.dueDate && <span className="flex items-center gap-1"><CalendarClock size={12} /> Due {fmtEdDate(t.dueDate, { month: 'short', day: 'numeric' })}</span>}
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {t.hasSource && (
                    <Button variant="outline" size="sm" data-testid="portal-task-doc-btn" onClick={() => viewDoc(t)} className="gap-1.5">
                      <FileText size={14} /> View Document
                    </Button>
                  )}
                  {t.completionType === 'SIGN' ? (
                    <Button data-testid="portal-task-sign-btn" size="sm" onClick={() => openSign(t)} className="bg-saffron hover:bg-saffron-hover gap-1.5">
                      <FileSignature size={14} /> Review &amp; Sign
                    </Button>
                  ) : t.completionType === 'FILE' || t.requiresFile ? (
                    <Button data-testid="portal-task-upload-btn" size="sm" onClick={() => setFileTask(t)} className="bg-saffron hover:bg-saffron-hover gap-1.5">
                      <Paperclip size={14} /> Upload &amp; Complete
                    </Button>
                  ) : (
                    <Button data-testid="portal-task-complete-btn" size="sm" disabled={busyId === t.id} onClick={() => completeSimple(t)} className="bg-coriander hover:bg-coriander/90 gap-1.5">
                      <CheckCircle2 size={14} /> {busyId === t.id ? 'Saving…' : 'Mark Complete'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {done.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-ink uppercase tracking-wide">Completed ({done.length})</p>
              {done.map((t) => (
                <div key={t.id} className="bg-white rounded-2xl border border-border/40 p-4 flex items-start justify-between gap-3" data-testid="portal-task-done-row">
                  <div className="flex items-start gap-2 min-w-0">
                    <CheckCircle2 size={16} className="text-coriander mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-charcoal truncate">{t.title}</p>
                      {t.completionType === 'SIGN' && t.lastSignature ? (
                        <p className="text-xs text-muted-ink">Signed {stamp(t.lastSignature.signedAt)}{t.lastSignature.expiresAt ? ` · valid until ${fmtEdDate(t.lastSignature.expiresAt, { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}</p>
                      ) : (
                        <p className="text-xs text-muted-ink">Completed {fmtEdDate(t.completedAt, { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                      )}
                    </div>
                  </div>
                  {t.hasCompletionFile && (
                    <Button variant="ghost" size="sm" onClick={() => viewProtectedFile(portalApi, `/staff/tasks/${t.id}/download`).catch((e) => toast.error(formatApiError(e)))} className="text-coriander gap-1.5 shrink-0">
                      <Download size={15} /> File
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <Dialog open={!!fileTask} onOpenChange={(o) => !o && setFileTask(null)}>
        <DialogContent data-testid="portal-task-upload-dialog">
          <DialogHeader><DialogTitle>{fileTask?.title}</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label>Upload the required file to complete this activity</Label>
            <input
              ref={fileRef}
              type="file"
              data-testid="portal-task-file-input"
              className="block w-full text-sm text-charcoal file:mr-3 file:rounded-lg file:border-0 file:bg-saffron file:px-3 file:py-1.5 file:text-white file:text-sm"
            />
          </div>
          <DialogFooter>
            <Button data-testid="portal-task-upload-submit-btn" disabled={uploadBusy} onClick={completeWithFile} className="bg-saffron hover:bg-saffron-hover">
              {uploadBusy ? 'Uploading…' : 'Upload & Complete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!signTask} onOpenChange={(o) => !o && setSignTask(null)}>
        <DialogContent data-testid="portal-task-sign-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{signTask?.title}</DialogTitle>
            <DialogDescription>Review the document, confirm you&apos;ve read it, then sign below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {signTask?.hasSource && (
              <Button variant="outline" size="sm" data-testid="sign-view-doc-btn" onClick={() => viewDoc(signTask)} className="gap-1.5 w-full">
                <FileText size={15} /> Open document to read
              </Button>
            )}
            <label className="flex items-start gap-2 text-sm text-charcoal cursor-pointer">
              <input
                type="checkbox"
                data-testid="sign-confirm-read"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="h-4 w-4 mt-0.5 accent-saffron"
              />
              I confirm I have read and understood the entire document.
            </label>

            {signTask?.signatureMethods === 'BOTH' && (
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={signMode === 'TYPED' ? 'default' : 'outline'} data-testid="sign-mode-typed" onClick={() => setSignMode('TYPED')} className={`gap-1.5 flex-1 ${signMode === 'TYPED' ? 'bg-saffron hover:bg-saffron-hover' : ''}`}>
                  <Type size={14} /> Type
                </Button>
                <Button type="button" size="sm" variant={signMode === 'DRAWN' ? 'default' : 'outline'} data-testid="sign-mode-drawn" onClick={() => setSignMode('DRAWN')} className={`gap-1.5 flex-1 ${signMode === 'DRAWN' ? 'bg-saffron hover:bg-saffron-hover' : ''}`}>
                  <PenLine size={14} /> Draw
                </Button>
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Full name</Label>
              <Input data-testid="sign-name-input" value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Your full legal name" />
            </div>

            {signMode === 'TYPED' ? (
              signerName && <p className="font-[cursive] text-2xl text-charcoal border-b border-border/50 pb-1" data-testid="sign-typed-preview">{signerName}</p>
            ) : (
              <div className="space-y-1.5">
                <Label>Draw your signature</Label>
                <SignaturePad apiRef={padRef} />
              </div>
            )}
            <p className="text-xs text-muted-ink">By signing you agree this is your electronic signature. It will be stamped with your name and the current date &amp; time (Edmonton).</p>
          </div>
          <DialogFooter>
            <Button data-testid="sign-submit-btn" disabled={signBusy} onClick={submitSign} className="bg-saffron hover:bg-saffron-hover">
              {signBusy ? 'Signing…' : 'Sign & Complete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
